// src/utils/htmlReadingAttrs.js

/**
 * Aplica no HTML canônico os atributos que são decisão de LEITURA, não de
 * conteúdo — os mesmos que `renderableAttrs` acrescenta em
 * `TextWithNotesHtml.vue` ao montar os vnodes do leitor.
 *
 * Existe porque os CARDS (`TextCardModal`, `ActivityCardModal`) não passam pelo
 * leitor: eles jogam a marcação direto num `v-html`, já que não há offset de
 * nota ancorado nela e o renderizador recursivo seria peso morto. O que se
 * perdia nesse atalho não era estética:
 *
 *   • link externo sem `target` SUBSTITUI a aplicação na aba — com um modal
 *     aberto por cima, o usuário perde o contexto inteiro num clique;
 *   • imagem é hotlink, então cada carga é uma requisição ao servidor de
 *     origem. Sem `referrerpolicy`, ele recebe no `Referer` a URL da página do
 *     Litterae — quem lê o quê vazando para terceiro a cada imagem.
 *
 * ⚠️ ESTE ARQUIVO É METADE DE UM PAR, como `htmlProjection.js`. A outra metade
 * é o `renderableAttrs` do leitor, do qual esta espelha as decisões de LINK e
 * IMAGEM. Divergir não gera erro: gera link que se comporta de um jeito no
 * leitor e de outro no card, para o mesmo conteúdo.
 *
 * O que este arquivo NÃO faz, de propósito:
 *
 *   • não filtra tag nem atributo. Isso é da whitelist do `HtmlNormalizer` no
 *     backend, aplicada antes de gravar. Refiltrar aqui daria a impressão de
 *     que o `v-html` é seguro POR CAUSA desta função, e ela não é isso;
 *   • não intercepta o clique para abrir em aba de fundo. O leitor faz, o card
 *     não: ali a leitura é contínua e voltar para ela importa, aqui o card é
 *     uma parada curta. Fica o `target="_blank"`, que é a rede do próprio
 *     leitor quando o handler não roda;
 *   • não converte `colwidth` em `style="width"`, que é a terceira decisão de
 *     leitura do `renderableAttrs`. O card mostra o corpo numa coluna estreita,
 *     onde a largura arrastada no editor não caberia de todo modo; com o
 *     `table-layout: fixed` da folha, as colunas dividem o espaço por igual.
 *     É a única divergência conhecida em relação ao leitor.
 */

/**
 * Prepara o HTML para ser injetado com `v-html` num card.
 *
 * @param {string} html HTML canônico, já normalizado pelo backend.
 * @returns {string} O mesmo HTML com os atributos de leitura aplicados.
 */
export function applyReadingAttrs(html) {
  if (!html) return "";

  // DOMParser em vez de innerHTML num elemento avulso: o documento resultante é
  // INERTE — `src` não dispara requisição e script nenhum roda enquanto
  // mexemos nele. O innerHTML de um <div> criado por `createElement` já
  // carregaria cada imagem só para lermos os atributos.
  const doc = new DOMParser().parseFromString(html, "text/html");

  for (const anchor of doc.querySelectorAll("a[href]")) {
    const href = anchor.getAttribute("href");

    // Âncora interna aponta para um `id` do corpo do texto. No leitor ela rola
    // a página; dentro de um card sobreposto não há para onde rolar — o alvo
    // pode nem estar no trecho exibido —, e um <a> vivo levaria o navegador a
    // trocar o hash da URL, mexendo na rota por baixo do modal. Some o `href`,
    // fica o texto: o CSS de `.text-body--html a` só pega `a`, então o
    // sublinhado pontilhado continua ali, sem prometer um salto que não vai
    // acontecer.
    if (href.startsWith("#")) {
      anchor.removeAttribute("href");
      continue;
    }

    anchor.setAttribute("target", "_blank");
    // noopener fecha o acesso da aba nova ao `window.opener`; noreferrer evita
    // vazar a URL de origem.
    anchor.setAttribute("rel", "noopener noreferrer");
  }

  for (const img of doc.querySelectorAll("img")) {
    img.setAttribute("referrerpolicy", "no-referrer");
    img.setAttribute("loading", "lazy");
    img.setAttribute("decoding", "async");
  }

  return doc.body.innerHTML;
}
